/* ============================================
   EMAIL TEMPLATES — THE POST-QUOTE FOLLOW-UP LADDER
   Asset: email-tpl-followups.js
   CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-08-19)

   Four emails, sent in order, to a customer who received a quote and went quiet.

   WHEN THE LADDER IS LIVE AT ALL. Ryan: "If we sent them a quote and the last email
   correspondence we shared was me sending them the quote, then the person who was in the
   TO field of the quote never responded, then that's when I'll send an email follow-up."
   So follow-up 1 needs a quote that actually went out BY EMAIL (not merely a PDF the rep
   downloaded), and silence since. Each rung then needs the rung below it sent, and silence
   since that one.

   ⛔ ANY INBOUND REPLY CLOSES THE LADDER, not just one from the To line. A reply from a
   CC'd colleague means the thread is alive, and mailing "did you get this?" into a live
   thread is the exact outcome the ladder exists to avoid. See _lastInboundAfter in the
   kernel for the full note.

   THEY ARE SHORT ON PURPOSE. Ryan: "They don't need to be super long or wordy, just
   personalized based on the phone call and day/time of the call." Every one of them is a
   greeting, an optional call reference, one or two sentences, one QUESTION, and a sign-off.
   ⚠️ Every email ends on a question - that is the whole mechanism. An email with nothing to
   answer gets nothing back.

   THEY ARE ONE THREAD. Ryan: "each email replies to our previous email ... they can scroll
   through and see email 4, then 3, then 2, then 1, then the quote sent email." So each
   build() returns replyToMessage pointing at the message below it, and the modal sends
   through /outlook-reply instead of starting a new conversation.
   ============================================ */
(function () {
  'use strict';

  var T = window.RQDTpl;
  var B = window.RQDTplBlocks;
  if (!T || !B) { console.error('[email-tpl] followups loaded before the kernel'); return; }

  var LADDER = ['follow-up-1', 'follow-up-2', 'follow-up-3', 'follow-up-4'];

  /* ============================================
     THE FOUR EMAILS, IN WORDS
     CUSTOM_HUMANCOPYEDIT_BY_CLAUDE (Ryan 2026-09-03)
     ============================================
     Editable from Abby Emails -> Templates & snippets without a deploy. The builders below now
     only DECIDE and ASSEMBLE: which sentence, in what order, and whether a paragraph runs at all.

     ⚠️ THE CALL TAIL IS A SENTENCE FRAGMENT, AND IT IS THE ONE EXCEPTION HERE. It is spliced
     into the shared call-reference sentence at {TAIL} ("I called and left you a voicemail
     LAST WEEK to follow up on the quote I sent over."), so it carries its own LEADING SPACE and
     no full stop. The editor says so on the box. It is a fragment because the sentence around it
     is shared by four emails and each one needs a different reason for calling; the alternative
     was four near-identical copies of the whole call sentence, which is the drift this file
     exists to avoid.

     ⛔ EVERY ONE OF THESE ENDS ON A QUESTION and that is the whole mechanism (see the file
     header). An edit that turns the question into a statement will still send; it will just stop
     working. Nothing can enforce that but a person reading it. */
  /* ⛔ ONE OWNER PER TEMPLATE, not one for the family. The four rungs share a file and a shape,
     but they are four rows in the picker and four things Ryan edits separately - so the editor
     hangs each sentence off the email it belongs to, and that only works if the owner IS the
     template id. The declarations still sit together, which is what keeps the whole ladder
     readable in one place. */
  var HELP = {
    QUOTE_WORD: '"quote" or "quotes", counted from what actually went out',
    IT:         '"it" or "them", matching QUOTE_WORD',
    IT_THEY:    '"it" or "they", matching QUOTE_WORD',
    WHEN:       'when the quote was sent, e.g. "last week". \u26a0\ufe0f NO leading space on this one, '
                + 'unlike the shared call sentence - the words around it supply them.',
    PRODUCTS:   'up to three product names, hyperlinked, or the category noun past that',
    TALLY:      'the counted campaign so far: "I have left you a couple of voicemails and emailed '
                + 'you a few times"',
    TAIL:       'spliced into the shared call sentence, so it carries its own LEADING SPACE and '
                + 'no full stop: " to follow up on the quote I sent over"',
  };

  /* -- 1: did it even arrive? -- */
  var C1 = T.definePhrases('follow-up-1', {
    'tail':            ' to follow up on the {QUOTE_WORD} I sent over',
    'arrived.dated':   'Please let me know if the {QUOTE_WORD} sent {WHEN} reached you. '
                       + 'I have attached {IT} again here just in case {IT_THEY} got caught by a filter.',
    'arrived.undated': 'Please let me know if the {QUOTE_WORD} I sent over reached you. '
                       + 'I have attached {IT} again here just in case {IT_THEY} got caught by a filter.',
    'reviewed':        'Have you had a chance to look {IT} over yet?',
  }, HELP);

  /* -- 2: what is the timeline? -- */
  var C2 = T.definePhrases('follow-up-2', {
    'tail':     ' to see where you landed on this',
    'reviewed': "Hopefully you've had a chance to review the {QUOTE_WORD} I sent over.",
    'ask':      'If you could please let me know your timeline for making a decision, '
                + 'or whether anything has shifted on your end, I would greatly appreciate it. '
                + 'It helps me line up lead times so nothing holds you up later.',
  }, HELP);

  /* -- 3: still in the market? -- */
  var C3 = T.definePhrases('follow-up-3', {
    'tail':            ' about this',
    'market.named':    'Are you still in the market for the {PRODUCTS}?',
    'market.unnamed':  'Are you still in the market for what we quoted?',
    'either':          'Either way is completely fine, I just do not want to keep chasing you '
                       + 'if the project has changed direction.',
  }, HELP);

  /* -- 4: the last one -- */
  var C4 = T.definePhrases('follow-up-4', {
    'open':            "Figured I'd follow up one more time on this.",
    'tally.counted':   '{TALLY} over the past few weeks, and I do not want to keep filling up your inbox.',
    'tally.uncounted': 'I have reached out a few times now, and I do not want to keep filling up your inbox.',
    'ask':             'Is this still something you are considering, or should I close it out on my end? '
                       + 'A one line reply either way is all I need, and if the timing is just off I am '
                       + 'happy to circle back whenever it suits you.',
  }, HELP);

  /* ============================================
     The gate, written once for all four rungs
     ============================================
     Rung 1 hangs off the quote send; rungs 2-4 hang off the rung below. Both are the same
     question asked of a different message, so there is one function and an index. */
  function _gateRung(st, idx) {
    var id = LADDER[idx];
    if (st.wasSent(id)) return 'Already sent to this customer.';

    var prevTs, prevLabel;
    if (idx === 0) {
      if (!st.quoteEmail) {
        return st.quoted
          ? 'The quote has not been emailed to them yet, so there is nothing to follow up on.'
          : 'Available once a quote has been emailed to this customer.';
      }
      prevTs = st.quoteEmail.ts;
      prevLabel = 'the quote';
    } else {
      prevTs = st.sentAt(LADDER[idx - 1]);
      if (!prevTs) return 'Send follow-up ' + idx + ' first.';
      prevLabel = 'follow-up ' + idx;
    }

    var reply = st.lastInboundAfter(prevTs);
    if (reply) {
      var w = T.whenPhrase(reply.ts, T.now());
      return 'They replied' + (w.phrase ? ' ' + w.phrase : '') + ', so this ladder is closed. Reply to them instead.';
    }
    return null;
  }

  /* ============================================
     The shared reading every rung builds from
     ============================================
     One place that answers "what has happened since we last wrote, and what are we
     following up ON" - so four templates cannot drift into four different ideas of it. */
  function _read(ctx, idx) {
    var ref = String((ctx && ctx.ref) || '').trim();
    return Promise.all([
      T.loadDetails(ref),
      T.loadMentionIndex(),
      T.ensureChain(ref),
      T.loadRepSettings(),
    ]).then(function (res) {
      var details = res[0] || null;
      var index = res[1] || [];
      var dq = T.dealByRef(ref);
      var deals = T.dealSet(ref);          // deduped: this one COUNTS things
      var warnings = [];

      var quoteEmail = T.quoteEmailSend(ref, deals);
      /* The message this email hangs under: the rung below, or the quote send for rung 1.
         Falls back to the most recent outbound email, which is what a rep who sent the
         quote by some other route (or before the ledger existed) actually has. */
      var parent = quoteEmail;
      var lastOut = T.lastOutboundEmail(deals);
      if (idx > 0 && lastOut && lastOut.emailMeta) {
        parent = { ts: lastOut.ts, messageId: lastOut.emailMeta.messageId || '',
                   ref: lastOut.originRef || ref, subject: lastOut.emailMeta.subject || '' };
      }
      if (!parent || !parent.messageId) {
        warnings.push('Could not find the earlier email to reply to, so this will start a new thread instead of continuing the old one.');
      }

      /* Two windows, and they are NOT the same. The call reference speaks to what has
         happened since we last WROTE; the tally in the final email counts the whole
         campaign since the quote went out. */
      var sinceLastWrite = (idx > 0 && parent) ? parent.ts : (quoteEmail ? quoteEmail.ts : 0);
      var contactRecent = T.contactSince(deals, sinceLastWrite);
      var contactAll = T.contactSince(deals, quoteEmail ? quoteEmail.ts : 0);

      /* ⛔ AN AUTOMATED RUNG MAKES NO CLAIM THAT COULD GO STALE (Ryan 2026-08-20).
         When a sequence is armed, rungs 2-4 are RENDERED NOW and sent up to 24 days later, so
         every sentence in them has to still be true then. Two are not:

           • the call reference — "I called and left you a voicemail yesterday" is a claim about
             a specific day, and by rung 4 that day is three weeks gone;
           • the tally in rung 4 — "I've emailed you a couple of times" counts what had happened
             at ARM time, so it undercounts by three emails, every time.

         Ryan's call was to drop both rather than to re-date them at send time: re-dating means
         rebuilding copy in the worker, which is exactly what killed assets/email-tpl-samples.js.
         ⚠️ Nulling the readings is all it takes — callReference and contactTally BOTH return
         null for an empty contact, which the builders already handle (rung 4 falls back to
         "I have reached out a few times now"). No branch in the copy, no second version of it.
         ⚠️ RUNG 1 NEVER SEES THIS FLAG: the rep sends it live, so it keeps every personal
         sentence it has always had. */
      if (ctx && ctx.automated) { contactRecent = null; contactAll = null; }

      var items = T.inquiryProducts(details, index);
      var first = B.customerFirstName(details, dq, ctx);
      var words = B.quoteWords(ctx, ref);
      /* The quote is already on the thread, so a follow-up counts what WENT OUT rather than
         what is pinned to this composer. */
      if (quoteEmail && quoteEmail.quoteFiles && quoteEmail.quoteFiles.length > 1) {
        words = { count: quoteEmail.quoteFiles.length, quote: 'quotes', pdf: 'PDFs', it: 'them', itemThey: 'they' };
      }

      return {
        ref: ref, details: details, dq: dq, deals: deals, index: index,
        first: first, items: items, words: words, warnings: warnings,
        quoteEmail: quoteEmail, parent: parent,
        contactRecent: contactRecent, contactAll: contactAll,
        settings: res[3] || {},
      };
    });
  }

  /* Every rung ends the same way, and the reply-threading + re-attach requests are the
     same shape, so the assembly is written once. */
  function _finish(r, paras, extra) {
    paras.push(B.signOff(r.first, false));
    var out = { paragraphs: paras, warnings: r.warnings };
    if (r.parent && r.parent.messageId) {
      out.replyToMessage = { ref: r.parent.ref || r.ref, messageId: r.parent.messageId };
    }
    if (extra) { Object.keys(extra).forEach(function (k) { out[k] = extra[k]; }); }
    return out;
  }

  /* The object of "are you still in the market for ___?" and "the quote for ___". Up to
     three products get named; past that the category noun stands in, because a question
     with five products in it stops being a question. */
  /* A call tail is spliced into the shared call sentence as a plain string, so the phrase's runs
     are flattened back. Lossless: a tail carries no links. */
  function _flat(runs) {
    return (runs || []).map(function (r) { return r.t; }).join('');
  }

  function _productObject(items) {
    if (!items || !items.length) return null;
    if (items.length <= 3) return B.inlineProducts(items);
    return [{ t: B.productNoun(items) }];
  }

  /* ============================================
     1 — did it even arrive?
     ============================================
     Ryan: "basically checking in to see if they even got the email ... the main question in
     this email is basically if they got the quote, and if they had a chance to review it
     yet." The re-attach is the point: a quote that went to spam is indistinguishable from a
     quote that was ignored, and only one of those is worth chasing. */
  function _build1(ctx) {
    return _read(ctx, 0).then(function (r) {
      var paras = [B.greeting(r.first)];

      /* CUSTOM_DELAYEDSTART_BY_CLAUDE (Ryan 2026-09-28) — scheduled days out on purpose, so the
         call is named by its weekday ("I called and left you a voicemail on Monday") instead of
         being dropped as stale. `ctx.nowMs` is already the send time; see callReference. */
      var call = B.callReference(r.contactRecent, _flat(C1['tail']({ QUOTE_WORD: r.words.quote })),
        null, { nameDay: !!(ctx && ctx.delayedStart) });
      if (call) paras.push(call);

      /* Ryan's wording, 2026-08-19. The ASK leads: "Please let me know if ..." is a
         request the reader can act on, where "I wanted to make sure ..." only described
         our own intention and left them nothing to do.
         ⚠️ Two renderings because the send date may be unknown, NOT because the wording differs
         on purpose - edit them together, which is what the group does on the tab. */
      var w = r.quoteEmail ? T.whenPhrase(r.quoteEmail.ts, T.now()) : { phrase: '' };
      var vals = { QUOTE_WORD: r.words.quote, IT: r.words.it, IT_THEY: r.words.itemThey };
      paras.push(w.phrase
        ? C1['arrived.dated'](Object.assign({ WHEN: w.phrase }, vals))
        : C1['arrived.undated'](vals));

      /* ⛔ "Did it come through alright" was dropped from this question when the sentence
         above started asking it. Two askings of one question in a four line email reads as
         a template that was not reread. What is left is the OTHER thing we want to know. */
      paras.push(C1['reviewed']({ IT: r.words.it }));

      var extra = {};
      if (r.quoteEmail && r.quoteEmail.messageId) {
        extra.attachFromMessage = { ref: r.quoteEmail.ref || r.ref, messageId: r.quoteEmail.messageId };
      } else {
        r.warnings.push('Could not find the email that carried the ' + r.words.quote + ', so nothing was re-attached. Attach it by hand.');
      }
      return _finish(r, paras, extra);
    });
  }

  /* ============================================
     2 — what is the timeline?
     ============================================
     Ryan: "hopefully you've had a chance to review the quote I sent you ... Remember, they
     may have already shared one, so now we're phrasing things in such a way to check if
     what they may (or may not) have shared with us is still accurate."
     ⚠️ Which is why it asks whether anything has SHIFTED rather than asking cold. Asking a
     customer for a timeline they already gave you reads as not having listened. */
  function _build2(ctx) {
    return _read(ctx, 1).then(function (r) {
      var paras = [B.greeting(r.first)];

      var call = B.callReference(r.contactRecent, _flat(C2['tail']({})));
      if (call) paras.push(call);

      paras.push(C2['reviewed']({ QUOTE_WORD: r.words.quote }));
      paras.push(C2['ask']({}));

      return _finish(r, paras);
    });
  }

  /* ============================================
     3 — are you still in the market?
     ============================================
     Ryan: "Checking in to see if they're still in the market with a direct question like
     'Are you still in the market for the xyz?'" The bluntest of the four on purpose: by now
     the soft asks have been ignored, and a yes/no question is the easiest thing to answer. */
  function _build3(ctx) {
    return _read(ctx, 2).then(function (r) {
      var paras = [B.greeting(r.first)];

      var call = B.callReference(r.contactRecent, _flat(C3['tail']({})));
      if (call) paras.push(call);

      /* ⚠️ {PRODUCTS} is RUNS, not a string - that is what carries the hyperlink on each product
         name into the middle of the question. */
      var obj = _productObject(r.items);
      if (obj) {
        paras.push(C3['market.named']({ PRODUCTS: obj }));
      } else {
        paras.push(C3['market.unnamed']({}));
        r.warnings.push('No products found on this deal, so the question does not name what they were looking at.');
      }
      paras.push(C3['either']({}));

      return _finish(r, paras);
    });
  }

  /* ============================================
     4 — the last one
     ============================================
     Ryan: "Figured I'd follow-up one more time. I've left you a ['couple of voicemails' or
     'a voicemail' or 'a couple of texts and voicemails' etc. -- just depends on what's
     recorded in the DD] and sent you a few emails."
     ⚠️ The tally is counted from the note log, so it cannot claim a campaign that did not
     happen: if nothing was logged, the sentence is simply left out. */
  function _build4(ctx) {
    return _read(ctx, 3).then(function (r) {
      var paras = [B.greeting(r.first)];

      paras.push(C4['open']({}));

      var tally = B.contactTally(r.contactAll);
      paras.push(tally
        ? C4['tally.counted']({ TALLY: tally })
        : C4['tally.uncounted']({}));

      paras.push(C4['ask']({}));

      return _finish(r, paras);
    });
  }

  /* ============================================
     Registration
     ============================================
     Registered in ladder order, which is the order they appear in the picker. */
  var DEFS = [
    { id: 'follow-up-1', icon: '1️⃣', label: 'Follow-up 1 - did the quote arrive?',
      hint: 'Re-attaches the quote, asks if it came through and was reviewed', build: _build1 },
    { id: 'follow-up-2', icon: '2️⃣', label: 'Follow-up 2 - what is the timeline?',
      hint: 'Asks for a decision timeline, or whether the one they gave has shifted', build: _build2 },
    { id: 'follow-up-3', icon: '3️⃣', label: 'Follow-up 3 - still in the market?',
      hint: 'The direct yes/no question, naming what they were looking at', build: _build3 },
    { id: 'follow-up-4', icon: '4️⃣', label: 'Follow-up 4 - last check-in',
      hint: 'Counts the calls, texts and emails; asks to close it out or keep going', build: _build4 },
  ];

  DEFS.forEach(function (d, i) {
    T.register({
      id: d.id, icon: d.icon, label: d.label, hint: d.hint, build: d.build,
      gate: function (st) { return _gateRung(st, i); },
    });
  });
})();
